import * as React from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { motion } from "motion/react";
import { AlertTriangle, CheckCircle2, Clock, Loader2 } from "lucide-react";
import { Button, FormRenderer } from "@eduviaos/ui-kit";
import { getCurrentTenant } from "@eduviaos/shared";
import type {
  FieldValidationError,
  PublicApplyFormResponse,
  PublicApplyFormSubmitResponse,
  PublicApplyMeResponse,
} from "@eduviaos/shared/types";
import { fetchApplyForm, submitApplyForm } from "@/lib/api";
import { adaptPublicFormToDefinition } from "@/lib/form-adapter";
import { parsePublicError, type PublicError } from "@/lib/error-utils";
import { formatTimeUntil } from "@/lib/format-time";
import { PageCard } from "@/components/AppFrame";
import { ErrorState } from "@/components/ErrorState";

/**
 * Phase F4 BE-2 — apply-form section of the apply portal.
 *
 * Wraps the same `<FormRenderer>` the public IntakePage uses, but binds
 * to the apply-token endpoints instead of the anonymous intake form.
 * The token is single-use; on successful submit the BE marks it
 * `consumed` and we render the success view (which itself surfaces the
 * post-submit stage so the parent knows what happens next).
 *
 * Refusals worth knowing:
 *   - APPLY_TOKEN_PURPOSE_MISMATCH: token isn't for `apply_form`. The
 *     parent dispatch in `ApplyPage.tsx` already filters this case so
 *     this section never renders for the wrong purpose.
 *   - STAGE_HAS_NO_FORM: the funnel stage doesn't have a linked form —
 *     IA staff hasn't finished configuring this stage yet. We surface a
 *     calm "ask the institution" message rather than a 500-style alarm.
 */
export function ApplyFormSection({ token, me }: { token: string; me: PublicApplyMeResponse }) {
  const tenant = React.useMemo(() => getCurrentTenant(), []);
  const tenantSlug = tenant.slug ?? "";
  const [serverFieldErrors, setServerFieldErrors] = React.useState<FieldValidationError[]>([]);
  const [success, setSuccess] = React.useState<PublicApplyFormSubmitResponse | null>(null);

  const formQuery = useQuery({
    queryKey: ["public-apply-form", tenantSlug, token],
    queryFn: () => fetchApplyForm(token),
    enabled: !!tenantSlug && !!token,
  });

  const submitMutation = useMutation({
    mutationFn: (values: Record<string, unknown>) => submitApplyForm(token, { values }),
    onSuccess: (data) => {
      setServerFieldErrors([]);
      setSuccess(data);
      if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
    },
    onError: (err) => {
      const parsed = parsePublicError(err);
      setServerFieldErrors(parsed.fieldErrors);
    },
  });

  if (formQuery.isLoading) {
    return <PendingState label="Loading form…" />;
  }

  if (formQuery.error) {
    const parsed = parsePublicError(formQuery.error);
    return <FormFetchError parsed={parsed} />;
  }

  const data = formQuery.data;
  if (!data) {
    return (
      <ErrorState
        title="Form unavailable"
        body="We couldn't load the form for this stage. Please refresh, or try again in a few minutes."
      />
    );
  }

  if (success) {
    return <FormSuccess success={success} />;
  }

  return (
    <FormBody
      data={data}
      tenantSlug={tenantSlug}
      expiresAt={me.action_authorised.expires_at}
      submitting={submitMutation.isPending}
      submitMutationError={submitMutation.isError ? submitMutation.error : null}
      serverFieldErrors={serverFieldErrors}
      onSubmit={(values) => submitMutation.mutate(values)}
    />
  );
}

// ── Sub-components ────────────────────────────────────────────────────

function FormBody({
  data,
  tenantSlug,
  expiresAt,
  submitting,
  submitMutationError,
  serverFieldErrors,
  onSubmit,
}: {
  data: PublicApplyFormResponse;
  tenantSlug: string;
  expiresAt: string | null;
  submitting: boolean;
  submitMutationError: unknown;
  serverFieldErrors: FieldValidationError[];
  onSubmit: (values: Record<string, unknown>) => void;
}) {
  const submitError =
    submitMutationError && serverFieldErrors.length === 0
      ? parsePublicError(submitMutationError)
      : null;

  const formDefinition = adaptPublicFormToDefinition(data.form, tenantSlug);

  return (
    <PageCard
      title={data.form.name}
      subtitle={`Stage: ${data.stage.label}. Fill in the details below to advance your application.`}
    >
      {expiresAt && (
        <div
          role="status"
          className="border-border/60 bg-muted/30 text-muted-foreground mb-4 flex items-center gap-2 rounded-md border p-2.5 text-xs"
        >
          <Clock className="text-foreground/70 h-3.5 w-3.5" />
          <span>
            This link will close in{" "}
            <span className="text-foreground font-medium">{formatTimeUntil(expiresAt)}</span>.
            Submit before then to keep your application moving.
          </span>
        </div>
      )}

      {submitError && (
        <div
          role="alert"
          className="border-destructive/40 bg-destructive/10 text-destructive mb-5 flex items-start gap-2 rounded-md border p-3 text-sm"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>{submitError.message}</span>
        </div>
      )}

      <FormRenderer
        form={formDefinition}
        serverErrors={serverFieldErrors}
        onSubmit={onSubmit}
        submitting={submitting}
      />
    </PageCard>
  );
}

function FormSuccess({ success }: { success: PublicApplyFormSubmitResponse }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.985, y: 6 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.36, ease: [0.32, 0.72, 0.32, 1] }}
    >
      <PageCard
        title={
          <span className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-emerald-500" />
            <span>Form submitted</span>
          </span>
        }
        subtitle={
          success.advanced
            ? `Your application has moved to the next stage: ${success.stage.label}.`
            : `Your form was received. Current stage: ${success.stage.label}.`
        }
      >
        <p className="text-foreground/90 text-sm">{success.success_message}</p>
        {success.stage.next_action_hint && (
          <p className="text-muted-foreground mt-3 text-xs">
            <span className="text-foreground font-medium">Next step:</span>{" "}
            {success.stage.next_action_hint}
          </p>
        )}
      </PageCard>
    </motion.div>
  );
}

function PendingState({ label }: { label: string }) {
  return (
    <PageCard>
      <div
        role="status"
        aria-live="polite"
        className="text-muted-foreground flex items-center gap-2 py-6 text-sm"
      >
        <Loader2 className="text-primary h-4 w-4 animate-spin" />
        <span>{label}</span>
      </div>
    </PageCard>
  );
}

function FormFetchError({ parsed }: { parsed: PublicError }) {
  if (parsed.code === "STAGE_HAS_NO_FORM") {
    return (
      <ErrorState
        title="Form not configured"
        body="The institution hasn't published a form for this stage yet. Please contact them and they'll get it sorted."
      />
    );
  }
  if (parsed.kind === "tenant_or_form_not_found") {
    return (
      <ErrorState
        title="Form unavailable"
        body="We couldn't find a form linked to this stage. Please contact the institution to confirm the apply link is current."
      />
    );
  }
  if (parsed.kind === "network") {
    return (
      <ErrorState
        title="Can't reach EduViaOS"
        body={parsed.message}
        cta={<Button onClick={() => window.location.reload()}>Try again</Button>}
      />
    );
  }
  return (
    <ErrorState
      title="Couldn't load form"
      body={parsed.message}
      cta={<Button onClick={() => window.location.reload()}>Reload</Button>}
    />
  );
}
